import React from "react";
import { Phone, Mail, Instagram, MessageCircle, MapPin, ChevronRight } from "lucide-react";
import { LOGO_URL, CONTACT } from "../data/mock";

const FOOTER_LINKS = {
  "Interior Designs": [
    { label: "Modular Kitchen", href: "#designs" },
    { label: "Living Room", href: "#designs" },
    { label: "Bedroom", href: "#designs" },
    { label: "Wardrobe", href: "#designs" },
    { label: "Pooja Room", href: "#designs" },
    { label: "Kids Bedroom", href: "#designs" },
  ],
  "Services": [
    { label: "3D Design Visualization", href: "#3d-design" },
    { label: "Modular Furniture", href: "#services" },
    { label: "Complete Home Interior", href: "#services" },
    { label: "1 BHK Interior", href: "#designs" },
    { label: "2 BHK Interior", href: "#designs" },
    { label: "3 BHK Interior", href: "#designs" },
  ],
  "Quick Links": [
    { label: "About Us", href: "#services" },
    { label: "Our Process", href: "#process" },
    { label: "Blog", href: "/blog" },
    { label: "Contact Us", href: "#contact" },
    { label: "FAQ", href: "#faq" },
  ],
};

export function Footer({ onGetQuote }) {
  return (
    <footer className="bg-[#111111] text-white" data-testid="footer">
      {/* CTA Band */}
      <div className="bg-[#C9A84C] py-10 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-white">
              Ready to Transform Your Hyderabad Home?
            </h3>
            <p className="text-white/80 mt-1">Book a free consultation today — no obligation, just great design ideas.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onGetQuote}
              className="bg-white text-[#C9A84C] font-bold px-7 py-3 rounded-full hover:bg-gray-100 transition-colors flex items-center gap-2"
              data-testid="footer-quote-btn"
            >
              Get Free Quote <ChevronRight size={16} />
            </button>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(CONTACT.whatsappMsg)}`}
              target="_blank"
              rel="noreferrer"
              className="bg-[#25D366] text-white font-bold px-7 py-3 rounded-full hover:bg-[#1fb856] transition-colors flex items-center gap-2"
              data-testid="footer-whatsapp-btn"
            >
              <MessageCircle size={16} />
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <img src={LOGO_URL} alt="Good Idea Interiors" className="h-16 w-auto object-contain mb-4 brightness-150" />
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
              Hyderabad's trusted interior designers. We transform homes with stunning designs,
              premium materials, and flawless execution. Good Design, Better Living.
            </p>
            <div className="space-y-3">
              <a
                href={`tel:${CONTACT.phone}`}
                className="flex items-center gap-3 text-gray-400 hover:text-[#C9A84C] transition-colors text-sm"
                data-testid="footer-phone"
              >
                <Phone size={15} className="text-[#C9A84C]" />
                {CONTACT.phone}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-3 text-gray-400 hover:text-[#C9A84C] transition-colors text-sm"
                data-testid="footer-email"
              >
                <Mail size={15} className="text-[#C9A84C]" />
                {CONTACT.email}
              </a>
              <a
                href={`https://www.instagram.com/${CONTACT.instagram}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-gray-400 hover:text-[#C9A84C] transition-colors text-sm"
                data-testid="footer-instagram"
              >
                <Instagram size={15} className="text-[#C9A84C]" />
                @{CONTACT.instagram}
              </a>
              <div className="flex items-center gap-3 text-gray-400 text-sm">
                <MapPin size={15} className="text-[#C9A84C]" />
                Hyderabad, Telangana
              </div>
            </div>
          </div>

          {/* Links columns */}
          {Object.entries(FOOTER_LINKS).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-semibold text-white mb-4">{title}</h4>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-[#C9A84C] text-sm transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5 py-5 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-gray-500">
          <p>© 2026 Good Idea Interiors. All rights reserved. | Hyderabad, Telangana</p>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-[#C9A84C] transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-[#C9A84C] transition-colors">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
